import { computed } from "vue";
import { defineStore } from "pinia";

import { useSceneStore } from "./scenes.store";
import { useSceneNodesStore } from "./sceneNodes.store";

import { isElementTreeNode, isSceneElementNode } from "../sceneElement/sceneElementFunctions";
import { isSceneLayerNode, isLayerTreeNode } from "../sceneLayer/sceneLayerFunctions";
import { usePresenceStore } from "@editor/store/presence.store";
import { useNetStore } from "@editor/net/net.store";

import type { Uid } from "@/helpers/utility/Id";
import type { SceneNodeType } from "./SceneNodeData";
import type { SceneTreeNode } from "./SceneTreeItem";
import { useFlowStore } from "../flow/flows.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { useSceneFlowNodeStore } from "../flow/sceneFlowNodes";

export const useSceneTree = defineStore("net/sceneTree", () => {
  const sceneStore = useSceneStore();
  const sceneNodeStore = useSceneNodesStore();
  const presence = usePresenceStore();

  const activeScene = computed(() => sceneStore.activeScene);

  const layerNodes = computed(() => {
    const sceneId = activeScene.value?.id;
    if (sceneId == null) return [];

    const sceneNodes = sceneNodeStore.byScene[sceneId] || [];

    // Build a map of nodes by their IDs
    const nodeMap: Record<Uid<"sceneNode">, SceneTreeNode> = {};
    for (const node of sceneNodes) {
      nodeMap[node.uid] = {
        type: "sceneNode",
        uid: node.uid,
        id: node.id,

        node,

        visible: node.visible,
        opacity: node.opacity,
        locked: node.locked,
        
        treePosition: "",
        level: 0,
        order: node.order,
        parentUid: node.parentSceneNodeUid,
        path: [],

        superLocked: false,
        displayPosition: isSceneLayerNode(node) ? node.data.layerType : "scene",

        children: []
      };
    }

    // Build the tree structure
    const layerNodes: SceneTreeNode<"layer">[] = [];
    for (const node of sceneNodes) {
      const treeNode = nodeMap[node.uid];
      if (node.parentSceneNodeUid === null) {
        if (!isLayerTreeNode(treeNode)) {
          throw new Error("Top-level scene nodes must be of type 'layer'.");
        }
        layerNodes.push(treeNode);
      } else {
        const parentNode = nodeMap[node.parentSceneNodeUid];
        if (parentNode) {
          parentNode.children.push(treeNode as SceneTreeNode<Exclude<SceneNodeType, "layer">>);
        }
      }
    }

    // Assign tree positions
    function assignTreePositions(nodes: SceneTreeNode[], prefix: string) {
      nodes.sort((a, b) => {
        return a.order < b.order ? -1 : 1;
      });
      nodes.forEach((node, index) => {
        const position = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
        node.treePosition = position;
        node.level = position.split(".").length - 1;
        const parent = node.parentUid !== null ? nodeMap[node.parentUid] : null;
        node.path = parent ? [...parent.path, node.uid] : [node.uid];
        if (parent) {
          node.displayPosition = parent.displayPosition;
        }

        assignTreePositions(node.children, position);
      });
    }
    assignTreePositions(layerNodes, "");

    // Set visibility, opacity, and locked status from SceneNode properties
    function applyNodeProperties(nodes: SceneTreeNode[]) {
      for (const node of nodes) {
        const parentNode = node.parentUid !== null ? nodeMap[node.parentUid] : null;
        if (parentNode) {
          node.visible = isVisible(node, nodeMap) && parentNode.visible;
          node.opacity = node.opacity * parentNode.opacity;
          node.locked = parentNode.locked || isLocked(node);
          node.superLocked = parentNode.superLocked || (isLayerTreeNode(parentNode) && parentNode.locked);
        }

        applyNodeProperties(node.children);
      }
    }
    applyNodeProperties(layerNodes);

    return layerNodes;
  });

  const treeNodes = computed(() => {
    const nodes = new Map<Uid<"sceneNode">, SceneTreeNode>();
    function traverse(node: SceneTreeNode) {
      nodes.set(node.uid, node);
      for (const child of node.children) {
        traverse(child);
      }
    }
    for (const sceneLayerNode of layerNodes.value) {
      traverse(sceneLayerNode);
    }
    return nodes;
  });

  // ================================
  // ELEMENTS

  const elementNodes = computed(() => {
    const nodes = new Map<Uid<"index" | "target" | "resource">, SceneTreeNode<"element">>();
    for (const treeNode of treeNodes.value.values()) {
      if (!isElementTreeNode(treeNode)) continue;
      const data = treeNode.node.data;
      const elementUid = data.elementUid;
      nodes.set(elementUid, treeNode);
    }
    return nodes;
  });

  const elementColors = computed(() => {
    const colors: Record<Uid<"index" | "target" | "resource">, string | null | undefined> = {};

    const sceneNodes = sceneNodeStore.byScene[activeScene.value.id] || [];

    for (const sceneNode of sceneNodes) {
      if (!isSceneElementNode(sceneNode)) continue;
      const data = sceneNode.data;
      const elementUid = data.elementUid;
      colors[elementUid] = sceneNode.fill;
    }
    return colors;
  });

  // ================================
  // NODE PROPERTIES

  /**
   * Checks if the item is locked.
   * @param item The item to check.
   * @returns True if the item is locked, false otherwise.
   */
  function isLocked(node: SceneTreeNode): boolean {
    return node.locked;
  }

  /**
   * Is the item in the canvas and has it been provided to the canvas component?
   */
  function isVisible(node: SceneTreeNode, sceneNodes: Record<number, SceneTreeNode>): boolean {
    if (node.visible == false) return false;

    // Flow nodes have special visibility rules.
    // if (node.nodeType == "flow") {
    //   if (activeScene.value.flowRelationsDisplay == "hidden") return false;

    //   const flowId = splitUid(node.itemUid).id;

    //   // Only show the flow if at least one of its branches is visible
    //   const branches = flowBranchStore.byFlow[flowId];
    //   if (!branches) return false;

    //   return branches.some((branch) => {
    //     const elementUid = `${branch.elementType}_${branch.elementId}`;

    //     // Problem: flow branches are attached to an element, but the element might not be in
    //     // the current scene at all. So we need to check if the element are in the scene
    //     // by finding their scene nodes.
    //     const elementSceneNodes = Object.values(sceneNodes).filter((sn) => sn.itemUid == elementUid);
    //     if (elementSceneNodes.length == 0) return false;

    //     // If at least one of the element's scene nodes is visible, we consider the element visible.

    //     return true;
    //   });
    // }

    return true;
  }

  // ================================
  // FLOWS

  const flowStore = useFlowStore();
  const flowBranchStore = useFlowBranchStore();
  const sceneFlowNodeStore = useSceneFlowNodeStore();

  const visibleFlows = computed(() => {
    const scene = activeScene.value;
    if (!scene) return [];
    if (scene.flowRelationsDisplay == "hidden") return [];
    return flowStore.orderedItems.filter((flow) => {
      const flowNode = sceneFlowNodeStore.byScene[scene.id]?.[flow.id];
      if (flowNode && !flowNode.visible) return false;
      // Only show the flow if at least one of its branches is visible
      const branches = flowBranchStore.byFlow[flow.id];
      if (!branches) return false;
      return branches.some((branch) => {
        const elementUid = `${branch.elementType}_${branch.elementId}` as Uid<"target" | "resource" | "index">;
        const node = elementNodes.value.get(elementUid);
        return node && node.visible;
      });
    });
  });

  // ================================
  // SCENE POSITION AND TRANSFORM

  const netTransform = computed(() => {
    if (activeScene.value == null) {
      return {
        translateX: 0,
        translateY: 0,
        scale: 1
      };
    }
    const presenceTransform = presence.sceneTransforms?.[activeScene.value.id];
    return {
      translateX: presenceTransform?.translateX ?? activeScene.value.translateX,
      translateY: presenceTransform?.translateY ?? activeScene.value.translateY,
      scale: presenceTransform?.scale ?? activeScene.value.scale
    };
  });

  function updateNetTransform(transform: { translateX?: number; translateY?: number; scale?: number }) {
    const newTransform = {
      translateX: transform.translateX ?? netTransform.value.translateX,
      translateY: transform.translateY ?? netTransform.value.translateY,
      scale: transform.scale ?? netTransform.value.scale
    };
    // As long as we're single-user only, we can directly update the scene store.
    // In the future, this will have to be an active action that must be chosen by the user.
    sceneStore.update({
      id: activeScene.value.id,
      translateX: newTransform.translateX,
      translateY: newTransform.translateY,
      scale: newTransform.scale
    }, {
      _undoable: false
    });
    presence.setRecordEntry("sceneTransforms", activeScene.value.id, newTransform);
  }

  function centerOnNode(nodeUid: Uid<"sceneNode">) {
    const treeNode = treeNodes.value.get(nodeUid);
    if (!treeNode) return;
    const node = treeNode.node;

    const net = useNetStore();
    const canvasDimensions = net.canvasDimensions;
    if (!canvasDimensions) return;

    const canvasWidth = 1000;
    const canvasHeight = 700;
    const nodeCenterX = node.x + node.width / 2;
    const nodeCenterY = node.y + node.height / 2;
    const translateX = canvasWidth / 2 - nodeCenterX;
    const translateY = canvasHeight / 2 - nodeCenterY;

    updateNetTransform({
      translateX,
      translateY
    });
  }

  return {
    activeScene,

    visibleFlows,

    layerNodes,
    treeNodes,
    
    elementNodes,
    elementColors,

    netTransform,
    updateNetTransform,
    centerOnNode
  }
});

